import { api } from "_shared/_stores/api";
import SearchResults from "_shared/_api/search/search-results";
import Guid from "_shared/guids/guid";
import MarketplaceSearchResponseSchema, { MarketplaceSearchInfoJSON } from "marketplace/_schema/marketplace-search-response-schema";
import { marketplaceSearchResponseMiddleware } from "marketplace/_api/marketplace-search-response-middleware";
import MarketplaceCompanyAPIRequestSchema from "marketplace/companies/_api/_schema/marketplace-company-api-request-schema";
import MarketplaceCompanySearchSchema, {
  MarketplaceCompanySearchJSON,
} from "marketplace/companies/_api/_schema/marketplace-company-search-schema";
import { buildUpdateCompanyProfilePayload } from "marketplace/companies/profiles/_schema/update-company-profile-request-schema";
import Company from "marketplace/companies/company";
import CompanySchema, { CompanyJSON } from "marketplace/companies/company-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import ForumSchema, { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import z from "zod";

export const companiesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCompanyVendors: build.query<CompanyJSON[], void>({
      query: () => "marketplace/company-vendors",
      rawResponseSchema: z.array(CompanySchema.CompanyJSONZ),
      providesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    getCompanyById: build.query<CompanyJSON, { companyId: string }>({
      query: ({ companyId }) => `marketplace/companies/${companyId}`,
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      providesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    getCompaniesByIds: build.query<CompanyJSON[], { ids: string[] }>({
      query: ({ ids }) => ({
        url: "marketplace/companies-by-ids",
        method: "POST",
        body: ids,
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: z.array(CompanySchema.CompanyJSONZ),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Company" as const, id })),
              { type: "Company" as const, id: "LIST" },
            ]
          : [{ type: "Company" as const, id: "LIST" }],
    }),

    searchCompanies: build.query<SearchResults<MarketplaceSearchInfoJSON>, { search: MarketplaceCompanySearchJSON }>({
      query: ({ search }) => ({
        url: "marketplace/companies",
        params: MarketplaceCompanySearchSchema.toSearchParams(search),
      }),
      rawResponseSchema: MarketplaceSearchResponseSchema.ResponseJSONZ,
      transformResponse: marketplaceSearchResponseMiddleware,
      serializeQueryArgs: ({ queryArgs }) => MarketplaceCompanySearchSchema.toCacheKey(queryArgs.search),
      providesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    getCompanyAvatar: build.query<string, { companyId: string }>({
      query: ({ companyId }) => `marketplace/companies/${companyId}/avatar`,
      providesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    getCompanyContactForums: build.query<ForumJSON[], { companyId: string }>({
      query: ({ companyId }) => `marketplace/companies/${companyId}/contact`,
      rawResponseSchema: z.array(ForumSchema.ForumJSONZ),
    }),

    createCompany: build.mutation<CompanyJSON, { company: Company }>({
      query: ({ company }) => ({
        url: "marketplace/companies",
        method: "POST",
        body: MarketplaceCompanyAPIRequestSchema.serialize(company),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      invalidatesTags: [{ type: "Company" as const, id: "LIST" }],
    }),

    createCompanyAvatar: build.mutation<void, { companyId: string; file: File }>({
      query: ({ companyId, file }) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: `marketplace/companies/${companyId}/avatar`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { companyId }) => [{ type: "Company" as const, id: companyId }],
    }),

    updateCompany: build.mutation<
      CompanyJSON,
      {
        companyId: string;
        isVisible: boolean;
        contactId?: Guid;
        originalProfile?: CompanyProfile;
        updatedProfile?: CompanyProfile;
      }
    >({
      query: ({ companyId, isVisible, contactId, originalProfile, updatedProfile }) => ({
        url: `marketplace/companies/${companyId}`,
        method: "PATCH",
        body: buildUpdateCompanyProfilePayload(isVisible, contactId, originalProfile, updatedProfile),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: CompanySchema.CompanyJSONZ,
      invalidatesTags: (_result, _error, { companyId }) => [
        { type: "Company" as const, id: companyId },
        { type: "Company" as const, id: "LIST" },
      ],
    }),

    getCompanyContactForum: build.mutation<ForumJSON, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `marketplace/companies/${companyId}/contact`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ForumSchema.ForumJSONZ,
    }),
  }),
});

export const {
  useGetCompanyVendorsQuery,
  useGetCompanyByIdQuery,
  useGetCompaniesByIdsQuery,
  useSearchCompaniesQuery,
  useGetCompanyAvatarQuery,
  useGetCompanyContactForumsQuery,
  useCreateCompanyMutation,
  useCreateCompanyAvatarMutation,
  useUpdateCompanyMutation,
  useGetCompanyContactForumMutation,
} = companiesApi;
